import {reactive} from 'vue';

export type AuthEmailPasswordMode =
    'sign-in' | 'register' | 'register-sent' | 'forgot-password' | 'forgot-password-sent' | 'reset-password';

type AuthEmailPasswordResponse = {
    result?: {
        session?: unknown | null;
        user?: {
            identities?: unknown[];
        } | null;
    } | null;
    error: {
        message: string;
    } | null;
};

type AuthEmailPasswordDependencies = {
    signInWithPassword: (email: string, password: string) => Promise<AuthEmailPasswordResponse>;
    signUpWithPassword: (email: string, password: string) => Promise<AuthEmailPasswordResponse>;
    sendPasswordReset: (email: string) => Promise<AuthEmailPasswordResponse>;
    updatePassword: (password: string) => Promise<AuthEmailPasswordResponse>;
    onPasswordRecovery: (callback: () => void) => () => void;
    navigateToAuthenticatedApp: () => Promise<void> | void;
    logEvent: (eventName: string, option?: string | number) => void;
};

type AuthEmailPasswordOptions = {
    initialMode?: AuthEmailPasswordMode;
};

export type AuthEmailPasswordState = {
    mode: AuthEmailPasswordMode;
    email: string;
    password: string;
    confirmPassword: string;
    isLoading: boolean;
    errorMessage: string;
    successMessage: string;
};

export type AuthEmailPasswordContent = {
    title: string;
    description: string;
    submitLabel: string;
};

export function isAuthEmailPasswordRecoveryMode(search: string, hash: string = '') {
    const searchParams = new URLSearchParams(search);
    const hashParams = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);

    return (
        searchParams.get('mode') === 'recovery' ||
        searchParams.get('type') === 'recovery' ||
        hashParams.get('type') === 'recovery'
    );
}

export type AuthEmailPasswordRecoveryError = {
    code: string;
    description: string;
};

export function getAuthEmailPasswordRecoveryError(
    search: string,
    hash: string = '',
): AuthEmailPasswordRecoveryError | null {
    const searchParams = new URLSearchParams(search);
    const hashParams = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);

    const error = searchParams.get('error') ?? hashParams.get('error');
    const code = searchParams.get('error_code') ?? hashParams.get('error_code');
    const description = searchParams.get('error_description') ?? hashParams.get('error_description');

    if (!error) {
        return null;
    }

    return {
        code: code ?? error,
        description: description ?? 'This password reset link is invalid or has expired.',
    };
}

export function getAuthEmailPasswordContent(mode: AuthEmailPasswordMode): AuthEmailPasswordContent {
    if (mode === 'forgot-password') {
        return {
            title: 'Reset your password',
            description: 'Enter your email and we will send you a secure reset link.',
            submitLabel: 'Request password reset',
        };
    }

    if (mode === 'reset-password') {
        return {
            title: 'Choose a new password',
            description: 'Set a new password for your Lopaka account.',
            submitLabel: 'Update password',
        };
    }

    if (mode === 'register') {
        return {
            title: 'Create your account',
            description: 'Create an account with your email and password.',
            submitLabel: 'Create an Account',
        };
    }

    return {
        title: 'Sign in to your account',
        description: 'Enter your email and password to continue.',
        submitLabel: 'Sign In',
    };
}

export type AuthEmailPasswordAutocomplete = {
    email: string;
    password: string;
};

/**
 * Autocomplete tokens that let the browser password manager recognize the account fields and
 * offer to save them: "current-password" for an existing credential, "new-password" for a created one.
 */
export function getAuthEmailPasswordAutocomplete(mode: AuthEmailPasswordMode): AuthEmailPasswordAutocomplete {
    return {
        email: 'username',
        password: mode === 'sign-in' ? 'current-password' : 'new-password',
    };
}

export function createAuthEmailPassword(
    dependencies: AuthEmailPasswordDependencies,
    options: AuthEmailPasswordOptions = {},
) {
    const state = reactive<AuthEmailPasswordState>({
        mode: options.initialMode ?? 'sign-in',
        email: '',
        password: '',
        confirmPassword: '',
        isLoading: false,
        errorMessage: '',
        successMessage: '',
    });

    let unsubscribePasswordRecovery = () => {};

    function clearFeedback() {
        state.errorMessage = '';
        state.successMessage = '';
    }

    function showSignIn() {
        state.mode = 'sign-in';
        state.password = '';
        state.confirmPassword = '';
        clearFeedback();
    }

    function showRegister() {
        state.mode = 'register';
        state.password = '';
        state.confirmPassword = '';
        clearFeedback();
    }

    function showForgotPassword() {
        state.mode = 'forgot-password';
        state.password = '';
        state.confirmPassword = '';
        clearFeedback();
    }

    function enterRecoveryMode() {
        state.mode = 'reset-password';
        state.password = '';
        state.confirmPassword = '';
        clearFeedback();
    }

    function goBackToSignIn() {
        state.mode = 'sign-in';
        state.password = '';
        state.confirmPassword = '';
        clearFeedback();
    }

    async function submit() {
        if (state.isLoading) {
            return;
        }

        clearFeedback();
        state.isLoading = true;

        try {
            const email = state.email.trim();

            if (state.mode === 'sign-in') {
                if (!email || !state.password) {
                    state.errorMessage = 'Enter your email and password.';
                    dependencies.logEvent('email_sign_in_failure', 'missing_credentials');
                    return;
                }

                const {error} = await dependencies.signInWithPassword(email, state.password);
                if (error) {
                    state.errorMessage = error.message;
                    dependencies.logEvent('email_sign_in_failure', 'invalid_credentials');
                    return;
                }

                dependencies.logEvent('email_sign_in_success');
                state.successMessage = 'You are being signed in.';
                await dependencies.navigateToAuthenticatedApp();
                return;
            }

            if (state.mode === 'register') {
                if (!email || !state.password) {
                    state.errorMessage = 'Enter your email and create a password.';
                    return;
                }

                const {result, error} = await dependencies.signUpWithPassword(email, state.password);
                if (error) {
                    state.errorMessage = error.message;
                    return;
                }

                if (result?.user?.identities?.length === 0) {
                    state.errorMessage = 'An account with this email already exists. Please sign in instead.';
                    return;
                }

                if (result?.session) {
                    dependencies.logEvent('email_register_success', 'session_created');
                    await dependencies.navigateToAuthenticatedApp();
                    return;
                }

                dependencies.logEvent('email_register_success', 'confirmation_required');
                state.mode = 'register-sent';
                return;
            }

            if (state.mode === 'forgot-password') {
                if (!email) {
                    state.errorMessage = 'Enter your email to reset the password.';
                    return;
                }

                const {error} = await dependencies.sendPasswordReset(email);
                if (error) {
                    state.errorMessage = error.message;
                    return;
                }

                dependencies.logEvent('password_reset_requested');
                state.mode = 'forgot-password-sent';
                state.successMessage = `We sent a password reset link to ${email}.`;
                return;
            }

            if (!state.password) {
                state.errorMessage = 'Enter a new password.';
                return;
            }

            if (state.password !== state.confirmPassword) {
                state.errorMessage = 'Passwords must match.';
                return;
            }

            const {error} = await dependencies.updatePassword(state.password);
            if (error) {
                state.errorMessage = error.message;
                return;
            }

            dependencies.logEvent('password_reset_completed');
            state.successMessage = 'Your password has been updated.';
            await dependencies.navigateToAuthenticatedApp();
        } finally {
            state.isLoading = false;
        }
    }

    function initialize() {
        unsubscribePasswordRecovery = dependencies.onPasswordRecovery(() => {
            enterRecoveryMode();
        });
    }

    function dispose() {
        unsubscribePasswordRecovery();
    }

    return {
        state,
        initialize,
        dispose,
        submit,
        goBackToSignIn,
        showSignIn,
        showRegister,
        showForgotPassword,
        enterRecoveryMode,
    };
}
